import { create } from 'zustand';
import type { Client } from '@microsoft/microsoft-graph-client';
import { UserConfig, createDefaultConfig } from '@/types/config';
import { readJson, writeJson } from '@/lib/graph/onedrive-helpers';

const CONFIG_PATH = 'config/user-config.json';

// Cache duration: 5 minutes (user can manually refresh if needed)
const CACHE_DURATION_MS = 5 * 60 * 1000;

interface ConfigState {
  config: UserConfig | null;
  isLoading: boolean;
  error: string | null;
  lastLoadedAt: number | null;
  
  // Actions
  loadConfig: (graph: Client, userId: string, forceRefresh?: boolean) => Promise<void>;
  updateConfig: (graph: Client, updates: Partial<UserConfig>) => Promise<void>;
  resetConfig: (graph: Client, userId: string) => Promise<void>;
  invalidateCache: () => void;
}

export const useConfigStore = create<ConfigState>((set, get) => ({
  config: null,
  isLoading: false,
  error: null,
  lastLoadedAt: null,

  loadConfig: async (graph: Client, userId: string, forceRefresh = false) => {
    const { config, lastLoadedAt, isLoading } = get();
    
    // Skip if already loading
    if (isLoading) return;
    
    // Check cache validity
    const now = Date.now();
    const isCacheValid = config && lastLoadedAt && (now - lastLoadedAt < CACHE_DURATION_MS);
    
    if (isCacheValid && !forceRefresh) {
      return; // Use cached data
    }
    
    set({ isLoading: true, error: null });
    
    try {
      let loadedConfig = await readJson<UserConfig>(graph, CONFIG_PATH);
      
      // Create default config if none exists
      if (!loadedConfig) {
        loadedConfig = createDefaultConfig(userId);
        await writeJson(graph, CONFIG_PATH, loadedConfig);
      }
      
      set({ config: loadedConfig, isLoading: false, lastLoadedAt: Date.now() });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to load configuration',
        isLoading: false 
      });
    }
  },

  updateConfig: async (graph: Client, updates: Partial<UserConfig>) => {
    const { config } = get();
    if (!config) return;

    const updatedConfig: UserConfig = {
      ...config,
      ...updates,
      updatedUtc: new Date().toISOString(),
    };

    set({ isLoading: true, error: null });
    
    try {
      await writeJson(graph, CONFIG_PATH, updatedConfig);
      set({ config: updatedConfig, isLoading: false, lastLoadedAt: Date.now() });
    } catch (error) {
      console.error('Failed to update config:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to save configuration',
        isLoading: false 
      });
    }
  },

  resetConfig: async (graph: Client, userId: string) => {
    const defaultConfig = createDefaultConfig(userId);
    
    set({ isLoading: true, error: null });
    
    try {
      await writeJson(graph, CONFIG_PATH, defaultConfig);
      set({ config: defaultConfig, isLoading: false, lastLoadedAt: Date.now() });
    } catch (error) {
      console.error('Failed to reset config:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to reset configuration',
        isLoading: false 
      });
    }
  },

  invalidateCache: () => {
    set({ lastLoadedAt: null });
  },
}));
